import { computed } from 'vue';
import { store } from './useDocumentRevisionsStore';
import type { SearchFilter, SearchParams, SortOrderOptional } from '@/shared/lib';
import type { DocumentRevision, Document } from '@/entities/Document';
import { useDocumentSearchTypeStore } from '@/shared/lib';
import { DOCUMENT_SEARCH_URL } from './const';

export default function useDocumentSearch(initialParams?: Partial<SearchParams> | any) {
  const documentStore = store();
  const { searchType } = useDocumentSearchTypeStore();

  const revisions = computed(() => documentStore.revisions);
  const allRevisions = computed(() => documentStore.allRevisions);
  const hasMoreRevisions = computed(() => documentStore.hasMoreRevisions);

  const documents = computed(() => {
    if (documentStore.searchParams?.searchUrl === DOCUMENT_SEARCH_URL) {
      return revisions.value as unknown as Document[];
    }

    return revisions.value.map((revision: DocumentRevision) => revision.document);
  });

  const isUsingFilters = computed(() => {
    if (!documentStore.searchParams) return false;
    return Object.values(documentStore.searchParams.filter).some((value) => !!value);
  });

  const isUsingOrder = computed(() => {
    if (!documentStore.searchParams) return false;
    return !!(documentStore.searchParams.sort?.sort || documentStore.searchParams.sort?.order);
  });

  const shouldShowNoResults = computed(() => {
    if (!documentStore.searchParams) return false;
    return (
      revisions.value.length === 0 &&
      documentStore.searchParams.search &&
      !isUsingFilters.value &&
      !isUsingOrder.value
    );
  });

  const shouldShowEmptyPage = computed(() => {
    if (!documentStore.searchParams) return true;
    return (
      revisions.value.length === 0 &&
      !documentStore.searchParams.search &&
      !isUsingFilters.value &&
      !isUsingOrder.value
    );
  });

  const shouldShowQuantitySelect = computed(() => {
    return revisions.value.length > 0 || isUsingFilters.value || isUsingOrder.value;
  });

  const performSearch = async (params: SearchParams, page = 1, append = false) => {
    return documentStore.performSearch(params, page, append);
  };

  const handleQuantityChange = async (value: string | number) => {
    const numericValue = Number(value);
    documentStore.setQuantity(numericValue);

    if (documentStore.searchParams) {
      const newParams = {
        ...documentStore.searchParams,
        amount: numericValue,
        page: 1,
      };
      await documentStore.performSearch(newParams);
    }
  };

  const loadMoreRevisions = async () => {
    return documentStore.loadMoreRevisions();
  };

  const removeRevisions = (deletedIds: number[]) => {
    documentStore.removeRevisions(deletedIds);
  };

  const clearCache = () => {
    documentStore.clearCache();
  };

  const refreshCurrentSearch = async () => {
    return documentStore.refreshCurrentSearch();
  };

  const initSearch = async (params?: SearchParams | any) => {
    if (params) {
      let searchParams: SearchParams;
      if (typeof params.sort === 'string' || typeof params.order === 'string') {
        searchParams = {
          search: params.search || '',
          searchType: params.searchType || searchType.value,
          sort: {
            sort: params.sort || undefined,
            order: (params.order as SortOrderOptional) || undefined,
          },
          filter: params.filter || {},
          page: params.page || 1,
          amount: params.amount || documentStore.selectedQuantity,
          searchUrl: params.searchUrl || '/document-revisions/search',
          context: params.context || 'gestao',
        };
      } else {
        searchParams = params;
      }
      await documentStore.performSearch(searchParams);
    } else if (initialParams && Object.keys(initialParams).length > 0) {
      let sort;
      if (typeof initialParams.sort === 'string' || typeof initialParams.order === 'string') {
        sort = {
          sort: initialParams.sort || undefined,
          order: (initialParams.order as SortOrderOptional) || undefined,
        };
      } else {
        sort = initialParams.sort || { sort: undefined, order: undefined };
      }

      const fullParams: SearchParams = {
        search: initialParams.search || initialParams.searchTerm || '',
        searchType: initialParams.searchType || searchType.value,
        sort,
        filter: initialParams.filter || {},
        page: initialParams.page || 1,
        amount: initialParams.amount || documentStore.selectedQuantity,
        searchUrl: initialParams.searchUrl || '/document-revisions/search',
        context: initialParams.context || 'gestao',
      };
      await documentStore.performSearch(fullParams);
    }
  };

  const buildSearchParams = (overrides: Partial<SearchParams>): SearchParams => {
    const current = documentStore.searchParams;
    return {
      search: overrides.search ?? current?.search ?? '',
      searchType: overrides.searchType ?? current?.searchType ?? searchType.value,
      sort: overrides.sort ?? current?.sort ?? { sort: undefined, order: undefined },
      filter: overrides.filter ?? current?.filter ?? {},
      page: overrides.page ?? 1,
      amount: overrides.amount ?? documentStore.selectedQuantity,
      searchUrl: overrides.searchUrl ?? current?.searchUrl ?? '/document-revisions/search',
      context: overrides.context ?? current?.context ?? 'gestao',
    };
  };

  const handleSearch = async (search: string) => {
    const newParams = buildSearchParams({ search, page: 1 });
    await documentStore.performSearch(newParams);
  };

  const handleSortOptions = async (sortCamp: string, orderFlow: string) => {
    const newParams = buildSearchParams({
      sort: { sort: sortCamp, order: orderFlow as SortOrderOptional },
      page: 1,
    });
    await documentStore.performSearch(newParams);
  };

  const handleFilter = async (filters: SearchFilter) => {
    const newParams = buildSearchParams({ filter: filters, page: 1 });
    await documentStore.performSearch(newParams);
  };

  const onPageChanged = async (page: number) => {
    if (documentStore.searchParams) {
      const newParams = { ...documentStore.searchParams, page };
      await documentStore.performSearch(newParams);
    }
  };

  const cleanup = () => {};

  const searchWithContext = async (term: string) => {
    const newParams = buildSearchParams({ search: term, page: 1 });
    await documentStore.performSearch(newParams);
  };

  return {
    // State do documentStore
    revisions,
    allRevisions,
    documents,
    metadata: computed(() => documentStore.metadata),
    loading: computed(() => documentStore.loading),
    loadingMore: computed(() => documentStore.loadingMore),
    hasMoreRevisions,
    selectedQuantity: computed(() => documentStore.selectedQuantity),
    currentPage: computed(() => documentStore.currentPage),
    isInfiniteScrollMode: computed(() => documentStore.isInfiniteScrollMode),
    hasResults: computed(() => revisions.value.length > 0),
    shouldShowTable: computed(
      () => revisions.value.length > 0 || isUsingFilters.value || isUsingOrder.value
    ),

    // Computed UI state
    isUsingFilters,
    isUsingOrder,
    shouldShowNoResults,
    shouldShowEmptyPage,
    shouldShowQuantitySelect,

    // Actions
    performSearch,
    searchDocuments: performSearch,
    handleQuantityChange,
    loadMoreRevisions,
    removeRevisions,
    clearCache,
    refreshCurrentSearch,
    initSearch,
    buildSearchParams,

    handleSearch,
    handleSortOptions,
    handleFilter,
    onPageChanged,
    cleanup,
    searchWithContext,

    // Store para acesso direto se necessário
    store: documentStore,
  };
}
